import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";
import { LogOut, LayoutList, FileText, Mail, Bookmark, Plus, ShieldCheck, UserRound, Palette, Sparkles, MessageCircle, Handshake, HelpCircle, Trash2, Search, Share2, PlayCircle, Briefcase } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { softDeleteWithUndo } from "@/lib/softDelete";
import JobsOverview from "@/components/JobsOverview";
import JobDetailSheet from "@/components/JobDetailSheet";
import BookmarksPanel from "@/components/BookmarksPanel";
import ProfilePanel from "@/components/ProfilePanel";
import CVPanel from "@/components/CVPanel";
import InterviewPrepSheet from "@/components/InterviewPrepSheet";
import LetterWizard from "@/components/letters/LetterWizard";
import EmailWizard from "@/components/EmailWizard";
import LetterEditor from "@/components/letters/LetterEditor";
import LettersPanel from "@/components/letters/LettersPanel";
import NewJobWizard from "@/components/NewJobWizard";
import TemplateManager from "@/components/templates/TemplateManager";
import ReferencesPanel from "@/components/ReferencesPanel";
import TicketModal from "@/components/TicketModal";
import OnboardingTour from "@/components/OnboardingTour";
import ProductTourModal from "@/components/ProductTourModal";
import TrashPanel from "@/components/TrashPanel";
import AdminNotificationBell from "@/components/AdminNotificationBell";
import JobSearchPanel from "@/components/JobSearchPanel";
import PortfoliosPanel from "@/components/PortfoliosPanel";
import RecommendDialog from "@/components/RecommendDialog";
import { useConfirm } from "@/components/ConfirmDialog";

const NAV = [
  { key: "overview", labelKey: "navOverview", Icon: LayoutList },
  { key: "cvs", labelKey: "navMyCvs", Icon: FileText },
  { key: "letters", labelKey: "navMyLetters", Icon: Mail },
  { key: "templates", labelKey: "navTemplates", Icon: Palette },
  { key: "references", labelKey: "navReferences", Icon: Handshake },
  { key: "bookmarks", labelKey: "navBookmarks", Icon: Bookmark },
  { key: "jobsearch", labelKey: "navJobSearch", Icon: Search },
  { key: "portfolios", labelKey: "navPortfolios", Icon: Briefcase },
  { key: "profile", labelKey: "navProfile", Icon: UserRound },
  { key: "trash", labelKey: "navTrash", Icon: Trash2 },
];

export default function Dashboard() {
  const confirm = useConfirm();
  const { user, logout } = useAuth();
  const { t } = useT();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(() => {
    // Deep-link support: /dashboard?view=profile opens the profile panel
    // directly (used by the /partner CTA to jump straight to the Affiliate
    // tab).
    const v = searchParams.get("view");
    return NAV.some((n) => n.key === v) ? v : "overview";
  });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Toggle between "active" (default — hides rejected + archived jobs) and
  // "archive" (rejected + manually-archived). See GET /jobs?view=… in
  // backend/server.py.
  const [jobsView, setJobsView] = useState("active");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [prepJob, setPrepJob] = useState(null);
  const [letterJob, setLetterJob] = useState(null);
  const [letterEditorId, setLetterEditorId] = useState(null);
  // When JobDetailSheet routes the user to the Match-Verbeter Wizard
  // for a linked letter (e.g. after flipping status to
  // 'Verduidelijking gevraagd'), we lift the "auto-open wizard" flag
  // through the letter editor so the wizard pops open on mount.
  const [letterEditorAutoImprove, setLetterEditorAutoImprove] = useState(false);
  const [emailJob, setEmailJob] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [productTourOpen, setProductTourOpen] = useState(false);
  const [productTourClip, setProductTourClip] = useState("cv");
  const [recommendOpen, setRecommendOpen] = useState(false);
  const [wizardInitialUrl, setWizardInitialUrl] = useState("");

  // Auto-launch the first-time tour when the user hasn't completed it yet.
  useEffect(() => {
    if (user && user.onboarding_completed === false) {
      setTourOpen(true);
    }
  }, [user]);

  // Allow child components (e.g. AffiliatePanel) to launch the standalone
  // Partner tour via a global custom event — avoids drilling a callback
  // through Profile → Affiliate.
  useEffect(() => {
    const onPartnerTour = () => setTourOpen("partner");
    document.addEventListener("open-partner-tour", onPartnerTour);
    return () => document.removeEventListener("open-partner-tour", onPartnerTour);
  }, []);

  // Deep-link support: `/dashboard?tour=<clip>` auto-opens the product
  // tour modal on the requested clip. Fires once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clip = (params.get("tour") || "").toLowerCase();
    if (["cv", "letter", "interview", "radar"].includes(clip)) {
      setProductTourClip(clip);
      setProductTourOpen(true);
    }
    // Match Radar digest deep-link: `/dashboard?job=<id>` auto-opens the
    // JobDetailSheet on the pointed-to vacancy so mail → detail feels
    // seamless. Also strips the param from the URL so a reload doesn't
    // re-open indefinitely.
    const jobParam = (params.get("job") || "").trim();
    if (jobParam) {
      setSelectedJobId(jobParam);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("job");
        window.history.replaceState({}, "", url.toString());
      } catch { /* ignore */ }
    }
  }, []);

  // Global paste catcher: when the user pastes an http(s):// URL anywhere on
  // the dashboard (outside inputs/textareas) and no modal/wizard/editor is
  // active, launch the new-job wizard with the URL pre-filled so it can
  // auto-scrape immediately. Turns a LinkedIn copy → dashboard paste into a
  // one-action job intake.
  useEffect(() => {
    const onPaste = (e) => {
      // Bail if any modal is already open or the paste is targeted at a
      // form input (respect the user's intent to paste into a field).
      if (wizardOpen || prepJob || letterJob || letterEditorId || emailJob || ticketOpen || tourOpen) return;
      const target = e.target;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      const text = e.clipboardData?.getData("text") || "";
      const clean = text.trim();
      if (!clean || !/^https?:\/\/\S+\.\S+/.test(clean)) return;
      setWizardInitialUrl(clean);
      setWizardOpen(true);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [wizardOpen, prepJob, letterJob, letterEditorId, emailJob, ticketOpen, tourOpen]);

  // Central nav helper — switching sidebar sections must also close any
  // full-screen overlay sheets (JobDetail, LetterEditor, wizards) that
  // were opened from the previous view. Without this the sheet stays
  // mounted on top of the new panel, and the user has to hit refresh
  // before the switch feels applied.
  const navTo = useCallback((next) => {
    setView(next);
    setSelectedJobId(null);
    setLetterEditorId(null);
    setLetterJob(null);
    setPrepJob(null);
    setEmailJob(null);
    setWizardOpen(false);
    setWizardInitialUrl("");
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/jobs", { params: { view: jobsView } });
      setJobs(data);
    } catch {
      toast.error(t("dashLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t, jobsView]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const handleArchive = async (id) => {
    try {
      await api.post(`/jobs/${id}/archive`);
      setJobs((prev) => prev.filter((j) => j.id !== id));
      toast.success(t("dashArchived"));
    } catch {
      toast.error(t("dashArchiveFailed"));
    }
  };

  const handleQuickApply = async (job) => {
    // Flip a "saved" job to "applied" with today's date — one-click promote
    // so users can quickly clear the "still to apply" pile once they've
    // sent off the letter/email offline.
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await api.put(`/jobs/${job.id}`, { status: "applied", date_applied: today });
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, ...data } : j)));
      toast.success(t("dashQuickAppliedToast", { title: job.job_title || "" }));
    } catch {
      toast.error(t("dashQuickApplyFailed"));
    }
  };

  const handleUnarchive = async (id) => {
    try {
      await api.post(`/jobs/${id}/unarchive`);
      setJobs((prev) => prev.filter((j) => j.id !== id));
      toast.success(t("dashUnarchived"));
    } catch {
      toast.error(t("dashArchiveFailed"));
    }
  };

  const handleBulkArchive = async (ids) => {
    if (!ids?.length) return;
    try {
      const { data } = await api.post("/jobs/bulk-archive", { ids });
      setJobs((prev) => prev.filter((j) => !ids.includes(j.id)));
      toast.success(t("dashBulkArchived", { count: data?.archived ?? ids.length }));
    } catch {
      toast.error(t("dashArchiveFailed"));
    }
  };

  const handleBulkUnarchive = async (ids) => {
    if (!ids?.length) return;
    try {
      const { data } = await api.post("/jobs/bulk-unarchive", { ids });
      setJobs((prev) => prev.filter((j) => !ids.includes(j.id)));
      toast.success(t("dashBulkUnarchived", { count: data?.unarchived ?? ids.length }));
    } catch {
      toast.error(t("dashArchiveFailed"));
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm(t("dashConfirmDelete")))) return;
    const removedIndex = jobs.findIndex((j) => j.id === id);
    const removedItem = jobs[removedIndex];
    if (!removedItem) return;
    softDeleteWithUndo({
      kind: "job",
      id,
      apiDelete: () => api.delete(`/jobs/${id}`),
      optimisticHide: () => setJobs((prev) => prev.filter((j) => j.id !== id)),
      restore: () => setJobs((prev) => {
        if (prev.some((j) => j.id === id)) return prev;
        const next = [...prev];
        next.splice(Math.min(removedIndex, next.length), 0, removedItem);
        return next;
      }),
      labels: {
        success: t("dashDeleted"),
        undone: t("rbUndone"),
        undo: t("undo"),
        error: t("dashDeleteFailed"),
      },
    });
  };

  return (
    <div className="ed-page min-h-screen lg:flex" data-testid="dashboard-shell">
      {/* Sidebar */}
      <aside className="lg:w-72 lg:shrink-0 border-b lg:border-b-0 lg:border-r border-neutral-200 lg:h-screen lg:sticky lg:top-0 flex flex-col bg-[#F9F8F6] z-30" data-testid="sidebar">
        <div className="px-6 py-6 flex items-center justify-center border-b border-neutral-200" data-testid="dashboard-title">
          <Logo size="lg" />
        </div>

        <div className="px-6 pt-6">
          <button
            data-testid="sidebar-new-job"
            onClick={() => setWizardOpen(true)}
            className="ed-btn w-full justify-center"
          >
            <Plus size={14} strokeWidth={1.5} /> {t("njwCta")}
          </button>
        </div>

        <nav className="px-3 py-6 flex lg:flex-col gap-1 overflow-x-auto" data-testid="sidebar-nav">
          {NAV.map(({ key, labelKey, Icon }) => (
            <button
              key={key}
              data-testid={`nav-${key}`}
              onClick={() => navTo(key)}
              className={`flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] whitespace-nowrap transition-colors text-left ${
                view === key
                  ? "bg-neutral-900 text-[#F9F8F6]"
                  : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-200/50"
              }`}
            >
              <Icon size={15} strokeWidth={1.5} />
              {t(labelKey)}
            </button>
          ))}
        </nav>

        <div className="mt-auto px-6 py-6 border-t border-neutral-200 space-y-4 hidden lg:block">
          <button
            data-testid="sidebar-ticket"
            onClick={() => setTicketOpen(true)}
            className="w-full flex items-center justify-center gap-2 border border-neutral-300 px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:border-neutral-900 hover:bg-neutral-50 transition-colors"
          >
            <MessageCircle size={12} strokeWidth={1.5} /> {t("navFeedback")}
          </button>
          <button
            data-testid="sidebar-tour"
            onClick={() => setTourOpen(true)}
            className="w-full flex items-center justify-center gap-2 border border-neutral-300 px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:border-neutral-900 hover:bg-neutral-50 transition-colors"
          >
            <HelpCircle size={12} strokeWidth={1.5} /> {t("navTour")}
          </button>
          {/* Product-tour sidebar button hidden per user request. */}
          {/*
          <button
            data-testid="sidebar-product-tour"
            onClick={() => setProductTourOpen(true)}
            className="w-full flex items-center justify-center gap-2 border border-neutral-900 bg-neutral-900 text-[#F9F8F6] px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:bg-neutral-700 transition-colors"
          >
            <PlayCircle size={12} strokeWidth={1.5} /> {t("navProductTour")}
          </button>
          */}
          <button
            data-testid="sidebar-recommend"
            onClick={() => setRecommendOpen(true)}
            className="w-full flex items-center justify-center gap-2 border border-neutral-300 px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:border-neutral-900 hover:bg-neutral-50 transition-colors"
          >
            <Share2 size={12} strokeWidth={1.5} /> {t("navRecommend")}
          </button>
          {user && !user.is_admin && user.subscription_status !== "active" && (
            <Link
              to="/pricing"
              data-testid="upgrade-button"
              className="flex items-center justify-center gap-2 bg-neutral-900 text-[#F9F8F6] px-3 py-2.5 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:bg-neutral-700 transition-colors"
            >
              <Sparkles size={12} strokeWidth={1.5} /> {t("subUpgradeNow")}
            </Link>
          )}
          {user?.subscription_status === "trial" && !user?.is_admin && (
            <Link
              to="/pricing"
              data-testid="trial-badge"
              className="block text-center border border-neutral-900 px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:bg-neutral-900 hover:text-[#F9F8F6] transition-colors"
            >
              {t("subTrialBadge", { days: user.days_left })}
            </Link>
          )}
          {user?.is_admin && (
            <div className="flex items-center gap-2">
              <Link
                to="/admin"
                data-testid="admin-link"
                className="flex-1 flex items-center justify-center gap-1.5 border border-neutral-300 px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:border-neutral-900 transition-colors"
              >
                <ShieldCheck size={12} strokeWidth={1.5} /> {t("subAdminLink")}
              </Link>
              <AdminNotificationBell />
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-neutral-200 shrink-0" data-testid="user-avatar" />
              )}
              <span className="font-mono text-xs text-neutral-600 truncate" data-testid="user-name">{user?.name}</span>
            </div>
            <LanguageToggle testId="dashboard-language-toggle" />
          </div>
          <button data-testid="logout-button" onClick={logout} className="ed-btn ed-btn-outline w-full justify-center">
            <LogOut size={14} strokeWidth={1.5} /> {t("logout")}
          </button>
        </div>

        {/* Mobile bottom row */}
        <div className="lg:hidden px-6 pb-4 flex items-center justify-between gap-3">
          <LanguageToggle testId="dashboard-language-toggle-mobile" />
          <div className="flex items-center gap-2">
            <button data-testid="sidebar-ticket-mobile" onClick={() => setTicketOpen(true)} className="p-2 border border-neutral-300" title={t("navFeedback")}>
              <MessageCircle size={14} strokeWidth={1.5} />
            </button>
            <button data-testid="sidebar-tour-mobile" onClick={() => setTourOpen(true)} className="p-2 border border-neutral-300" title={t("navTour")}>
              <HelpCircle size={14} strokeWidth={1.5} />
            </button>
            {user && !user.is_admin && user.subscription_status !== "active" && (
              <Link to="/pricing" className="p-2 bg-neutral-900 text-[#F9F8F6]" data-testid="upgrade-button-mobile" title={t("subUpgradeNow")}><Sparkles size={14} strokeWidth={1.5} /></Link>
            )}
            {user?.is_admin && (
              <>
                <Link to="/admin" className="p-2 border border-neutral-300" data-testid="admin-link-mobile"><ShieldCheck size={14} strokeWidth={1.5} /></Link>
                <AdminNotificationBell />
              </>
            )}
            <button data-testid="logout-button-mobile" onClick={logout} className="p-2 border border-neutral-300"><LogOut size={14} strokeWidth={1.5} /></button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <main className="max-w-6xl mx-auto px-6 sm:px-10 py-10 lg:py-14">
          {view === "overview" && (
            <JobsOverview
              jobs={jobs}
              loading={loading}
              jobsView={jobsView}
              onChangeView={setJobsView}
              onOpenJob={setSelectedJobId}
              onPrep={setPrepJob}
              onLetter={setLetterJob}
              onEmail={setEmailJob}
              onDelete={handleDelete}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onBulkArchive={handleBulkArchive}
              onBulkUnarchive={handleBulkUnarchive}
              onNewJob={() => setWizardOpen(true)}
              onRefresh={loadJobs}
              onGoToJobSearch={() => setView("jobsearch")}
              onQuickApply={handleQuickApply}
            />
          )}
          {view === "cvs" && <CVPanel onLaunchJobWizard={() => setWizardOpen(true)} />}
          {view === "letters" && (
            <section data-testid="letters-view">
              <p className="ed-eyebrow flex items-center gap-2 mb-5">
                <Mail size={12} strokeWidth={1.5} /> {t("ltTitle")}
              </p>
              <LettersPanel
                onRegistered={loadJobs}
                onOpenJob={(jobId) => { setView("overview"); setSelectedJobId(jobId); }}
                onNavigateToCvs={() => setView("cvs")}
              />
            </section>
          )}
          {view === "templates" && <TemplateManager />}
          {view === "references" && <ReferencesPanel />}
          {view === "bookmarks" && <BookmarksPanel />}
          {view === "jobsearch" && (
            <JobSearchPanel
              onJobCreated={loadJobs}
              onCreatedForLetter={(job) => setLetterJob(job)}
            />
          )}
          {view === "portfolios" && <PortfoliosPanel />}
          {view === "profile" && <ProfilePanel />}
          {view === "trash" && <TrashPanel />}
        </main>
      </div>

      {/* Sheets & wizards */}
      {wizardOpen && (
        <NewJobWizard
          initialUrl={wizardInitialUrl}
          onClose={() => { setWizardOpen(false); setWizardInitialUrl(""); }}
          onCreated={loadJobs}
          onLetter={(job) => { setWizardOpen(false); setLetterJob(job); }}
          onPrep={(job) => { setWizardOpen(false); setPrepJob(job); }}
          onGoToJobSearch={() => { setWizardOpen(false); setWizardInitialUrl(""); setView("jobsearch"); }}
        />
      )}
      <JobDetailSheet
        jobId={selectedJobId}
        onClose={() => setSelectedJobId(null)}
        onUpdated={loadJobs}
        onOpenJob={(nextId) => setSelectedJobId(nextId)}
        onOpenLetterEditor={(lid, opts) => {
          setSelectedJobId(null);
          setLetterEditorAutoImprove(!!opts?.autoOpenImprove);
          setLetterEditorId(lid);
        }}
      />
      <InterviewPrepSheet job={prepJob} onClose={() => setPrepJob(null)} />
      {letterJob && (
        <LetterWizard
          prefillJob={letterJob}
          onClose={() => setLetterJob(null)}
          onDone={(letter) => { setLetterJob(null); setLetterEditorId(letter.id); }}
        />
      )}
      {letterEditorId && (
        <LetterEditor
          letterId={letterEditorId}
          autoOpenImprove={letterEditorAutoImprove}
          onClose={() => { setLetterEditorId(null); setLetterEditorAutoImprove(false); }}
          onRegistered={loadJobs}
          onNavigateToCvs={() => { setLetterEditorId(null); setLetterEditorAutoImprove(false); setView("cvs"); }}
        />
      )}
      {emailJob && <EmailWizard job={emailJob} onClose={() => setEmailJob(null)} onFollowedUp={loadJobs} />}
      {ticketOpen && <TicketModal onClose={() => setTicketOpen(false)} />}
      {tourOpen && (
        <OnboardingTour
          variant={tourOpen === "partner" ? "partner" : "main"}
          onGoto={(v) => setView(v)}
          onClose={() => setTourOpen(false)}
        />
      )}
      <ProductTourModal
        open={productTourOpen}
        onClose={() => setProductTourOpen(false)}
        initialClip={productTourClip}
        source="dashboard"
      />
      <RecommendDialog
        open={recommendOpen}
        onClose={() => setRecommendOpen(false)}
        isAffiliate={!!user?.is_affiliate}
        onOptInAffiliate={() => { setView("profile"); }}
        plainShareUrl="https://jobgrid.nl"
      />
    </div>
  );
}
